import { useState, useRef, useCallback, useEffect } from "react"
import PropTypes from "prop-types"

function cleanupObserver(observerRef) {
  if (observerRef.current) {
    observerRef.current.disconnect()
    observerRef.current = null
  }
}

function updateDimensionsIfChanged(setDimensions, newWidth, newHeight) {
  setDimensions(prevDimensions => {
    if (newWidth !== prevDimensions.width || newHeight !== prevDimensions.height) {
      return { width: newWidth, height: newHeight }
    }
    return prevDimensions
  })
}

function createResizeObserver(setDimensions) {
  return new ResizeObserver((entries) => {
    const entry = entries[0]
    if (entry) {
      const { width, height } = entry.contentRect
      updateDimensionsIfChanged(setDimensions, width, height)
    }
  })
}

function getInitialDimensions(element) {
  const rect = element.getBoundingClientRect()
  return {
    width: rect.width,
    height: rect.height || 600, // fallback height
  }
}

function setupResizeObserver(element, observerRef, setDimensions) {
  observerRef.current = createResizeObserver(setDimensions)
  observerRef.current.observe(element)

  const initialDimensions = getInitialDimensions(element)
  setDimensions(initialDimensions)
}

function resizable(WrappedComponent) {
  function ResizableComponent(props) {
    const { height: propsHeight, ...otherProps } = props
    const [dimensions, setDimensions] = useState({ width: 400, height: 600 })
    const resizeObserverRef = useRef(null)
    const containerRef = useRef(null)

    const setContainer = useCallback((node) => {
      cleanupObserver(resizeObserverRef)
      containerRef.current = node

      if (node) {
        setupResizeObserver(node, resizeObserverRef, setDimensions)
      }
    }, [])

    // Cleanup on unmount
    useEffect(() => {
      return () => cleanupObserver(resizeObserverRef)
    }, [])

    const { width, height: stateHeight } = dimensions
    const height = propsHeight || stateHeight

    return <WrappedComponent
      resizableRef={setContainer}
      width={width}
      height={height}
      {...otherProps}
    />
  }

  ResizableComponent.displayName = `Resizable(${WrappedComponent.displayName || WrappedComponent.name})`

  ResizableComponent.propTypes = {
    height: PropTypes.number,
  }

  return ResizableComponent
}

export default resizable
